import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRealtimeSelfHeal } from "@/hooks/useRealtimeSelfHeal";
import { displayCompanyName } from "@/lib/displayCompanyName";

// Legacy `loadDirectQuoteRequest` writer helper removed — the RFQ creation
// flow (`LRDirectQuoteRequestNewPage`) was deleted. All new quote posting
// happens via LR Direct post page (`?mode=quote`).



export interface DirectQuoteRequest {
  id: string;
  shipper_company_id: string;
  created_by: string;
  network_id: string;
  origin_city: string;
  origin_state: string;
  origin_postal_code: string | null;
  destination_city: string;
  destination_state: string;
  destination_postal_code: string | null;
  equipment_type: string;
  weight_lbs: number | null;
  commodity: string | null;
  volume_pattern: "one_off" | "recurring" | "flexible";
  recurrence_note: string | null;
  pickup_window_start: string | null;
  pickup_window_end: string | null;
  target_rate: number | null;
  target_rate_visible_to_brokers: boolean;
  notes: string | null;
  audience: "full_network" | "specific";
  audience_broker_company_ids: string[];
  deadline: string;
  status: "open" | "closed" | "converted" | "expired" | "cancelled";
  awarded_response_id: string | null;
  converted_to_load_id: string | null;
  closed_at: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface DirectQuoteResponse {
  id: string;
  request_id: string | null;
  direct_load_id: string | null;
  broker_company_id: string;
  broker_user_id: string;
  rate: number;
  valid_until: string | null;
  capacity_note: string | null;
  alt_equipment: string | null;
  status: "active" | "withdrawn" | "awarded" | "declined" | "expired";
  withdrawn_at: string | null;
  withdraw_reason?: string | null;
  awarded_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Minimal broker identity surfaced on the shipper's My Quotes row.
 *  Only id + display_name + avatar path cross this boundary. */
export interface QuoteResponder {
  id: string;
  name: string | null;
  avatar_path: string | null;
}

/** Unified quote row (legacy RFQ + LR Direct quote posts). */
export interface UnifiedQuoteRow {
  id: string;
  source: "request" | "load";
  origin_city: string;
  origin_state: string;
  destination_city: string;
  destination_state: string;
  equipment_type: string;
  volume_pattern: "one_off" | "recurring" | "flexible" | null;
  notes: string | null;
  deadline: string;                                 // request.deadline OR load.bid_deadline
  status: string;                                   // free-text, tab logic normalizes below
  visibility: "private" | "open" | "both";          // request rows always private
  archived_at: string | null;
  created_at: string;
  response_count: number;
  low: number | null;
  avg: number | null;
  high: number | null;
  converted_to_load_id: string | null;
  responders: QuoteResponder[];
  total_miles: number | null;
}


/** Shipper-facing: unified list — legacy RFQs + LR Direct quote posts. */
export function useShipperQuoteRequests(companyId: string | undefined, userId?: string | undefined) {
  const [rows, setRows] = useState<UnifiedQuoteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!companyId && !userId) { setRows([]); setLoading(false); return; }
    setLoading(true);

    // ── Legacy RFQs ─────────────────────────────────────────────────────
    const reqPromise = companyId
      ? supabase
          .from("direct_quote_requests")
          .select("*")
          .eq("shipper_company_id", companyId)
          .order("created_at", { ascending: false })
      : Promise.resolve({ data: [] as any[], error: null });

    // ── LR Direct quote posts (post_type='quote') ───────────────────────
    const loadPromise = userId
      ? supabase
          .from("direct_loads" as any)
          .select("id, origin_city, origin_state, destination_city, destination_state, equipment_type, shipper_notes_to_brokers, bid_deadline, status, visibility_mode, deleted_at, converted_to_load_id, created_at, total_miles")
          .eq("created_by", userId)
          .eq("post_type", "quote")
          .is("deleted_at", null)
          .order("created_at", { ascending: false })
      : Promise.resolve({ data: [] as any[], error: null });

    const [reqRes, loadRes] = await Promise.all([reqPromise, loadPromise]);
    if (reqRes.error) { setError(reqRes.error.message); setLoading(false); return; }
    if ((loadRes as any).error) { setError((loadRes as any).error.message); setLoading(false); return; }

    const reqs = (reqRes.data ?? []) as any[];
    const loads = ((loadRes as any).data ?? []) as any[];

    const reqIds = reqs.map((r) => r.id);
    const loadIds = loads.map((l) => l.id);

    // ── Responses (single fetch, split by discriminator) ────────────────
    let respByReq = new Map<string, DirectQuoteResponse[]>();
    let respByLoad = new Map<string, DirectQuoteResponse[]>();
    if (reqIds.length || loadIds.length) {
      let query = supabase.from("direct_quote_responses").select("*");
      if (reqIds.length && loadIds.length) {
        query = query.or(
          `request_id.in.(${reqIds.join(",")}),direct_load_id.in.(${loadIds.join(",")})`
        );
      } else if (reqIds.length) {
        query = query.in("request_id", reqIds);
      } else {
        query = query.in("direct_load_id", loadIds);
      }
      const { data: resps } = await query;
      (resps ?? []).forEach((r: any) => {
        if (r.request_id) {
          const arr = respByReq.get(r.request_id) ?? [];
          arr.push(r as DirectQuoteResponse);
          respByReq.set(r.request_id, arr);
        } else if (r.direct_load_id) {
          const arr = respByLoad.get(r.direct_load_id) ?? [];
          arr.push(r as DirectQuoteResponse);
          respByLoad.set(r.direct_load_id, arr);
        }
      });
    }

    const statsFor = (all: DirectQuoteResponse[]) => {
      const active = all.filter((x) => x.status === "active" || x.status === "awarded");
      const rates = active.map((x) => Number(x.rate));
      return {
        response_count: active.length,
        low: rates.length ? Math.min(...rates) : null,
        avg: rates.length ? rates.reduce((a, b) => a + b, 0) / rates.length : null,
        high: rates.length ? Math.max(...rates) : null,
      };
    };

    // ── Responder identity (broker id + name + avatar path) ─────────────
    // Fetched via SECURITY DEFINER RPC scoped to caller's own quotes.
    // Same status filter as statsFor() so response_count and bubble count
    // reference the same broker set.
    const respondersByReq = new Map<string, QuoteResponder[]>();
    const respondersByLoad = new Map<string, QuoteResponder[]>();
    if (reqIds.length || loadIds.length) {
      const { data: responders } = await supabase.rpc("get_shipper_quote_responders", {
        p_request_ids: reqIds,
        p_direct_load_ids: loadIds,
      });
      (responders ?? []).forEach((row: any) => {
        const entry: QuoteResponder = {
          id: row.broker_user_id,
          name: row.display_name,
          avatar_path: row.avatar_url,
        };
        const map = row.source === "request" ? respondersByReq : respondersByLoad;
        const arr = map.get(row.parent_id) ?? [];
        arr.push(entry);
        map.set(row.parent_id, arr);
      });
    }

    const merged: UnifiedQuoteRow[] = [
      ...reqs.map((r) => ({
        id: r.id,
        source: "request" as const,
        origin_city: r.origin_city,
        origin_state: r.origin_state,
        destination_city: r.destination_city,
        destination_state: r.destination_state,
        equipment_type: r.equipment_type,
        volume_pattern: r.volume_pattern,
        notes: r.notes,
        deadline: r.deadline,
        status: r.status,
        visibility: "private" as const,
        archived_at: r.archived_at,
        created_at: r.created_at,
        converted_to_load_id: r.converted_to_load_id ?? null,
        responders: respondersByReq.get(r.id) ?? [],
        total_miles: null,
        ...statsFor(respByReq.get(r.id) ?? []),
      })),

      ...loads.map((l) => ({
        id: l.id,
        source: "load" as const,
        origin_city: l.origin_city,
        origin_state: l.origin_state,
        destination_city: l.destination_city,
        destination_state: l.destination_state,
        equipment_type: l.equipment_type,
        volume_pattern: null,
        notes: l.shipper_notes_to_brokers,
        deadline: l.bid_deadline ?? l.created_at,
        status: l.status,
        visibility:
          l.visibility_mode === "open_market" ? ("open" as const)
          : l.visibility_mode === "both" ? ("both" as const)
          : ("private" as const),
        archived_at: null,
        created_at: l.created_at,
        converted_to_load_id: l.converted_to_load_id ?? null,
        responders: respondersByLoad.get(l.id) ?? [],
        total_miles: l.total_miles ?? null,
        ...statsFor(respByLoad.get(l.id) ?? []),
      })),

    ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    setRows(merged);
    setError(null);
    setLoading(false);
  }, [companyId, userId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Realtime — refresh when a new quote response lands on any of our loads/requests
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`shipper-quote-responses-${userId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "direct_quote_responses" }, () => refresh())
      .subscribe((status) => { if (status === "SUBSCRIBED") refresh(); });
    return () => { supabase.removeChannel(channel); };
  }, [userId, refresh]);
  useRealtimeSelfHeal(refresh, !!userId);


  return { rows, loading, error, refresh };
}

/** Shipper-facing: single quote (RFQ or LR Direct quote post) + all responses + broker companies. */
export function useShipperQuoteRequestDetail(id: string | undefined) {
  const [source, setSource] = useState<"request" | "load" | null>(null);
  const [request, setRequest] = useState<DirectQuoteRequest | null>(null);
  const [loadPost, setLoadPost] = useState<any | null>(null);
  const [responses, setResponses] = useState<HydratedQuoteResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!id) return;
    setLoading(true);

    // Try legacy RFQ first
    const { data: req } = await supabase
      .from("direct_quote_requests").select("*").eq("id", id).maybeSingle();

    if (req) {
      setSource("request");
      setRequest(req as DirectQuoteRequest);
      setLoadPost(null);
      const { data: resps } = await supabase
        .from("direct_quote_responses").select("*").eq("request_id", id);
      await hydrateBrokerNames(resps ?? [], setResponses);
    } else {
      // Fall through to LR Direct quote post
      const { data: load, error: lErr } = await supabase
        .from("direct_loads" as any)
        .select("id, origin_city, origin_state, destination_city, destination_state, equipment_type, weight_lbs, commodity, pickup_date, delivery_date, bid_deadline, target_rate_min, target_rate_max, total_miles, shipper_notes_to_brokers, visibility_mode, private_network_id, status, post_type, converted_to_load_id, converted_at, load_number, deleted_at, created_by, created_at, posted_at")
        .eq("id", id)
        .eq("post_type", "quote")
        .maybeSingle();
      if (lErr) { setError(lErr.message); setLoading(false); return; }
      setSource("load");
      setLoadPost(load ?? null);
      setRequest(null);
      const { data: resps } = await supabase
        .from("direct_quote_responses").select("*").eq("direct_load_id", id);
      await hydrateBrokerNames(resps ?? [], setResponses);
    }
    setError(null);
    setLoading(false);
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  // Realtime on quote responses for this id
  useEffect(() => {
    if (!id) return;
    const channel = supabase
      .channel(`shipper-quote-detail-${id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "direct_quote_responses" }, () => refresh())
      .subscribe((status) => { if (status === "SUBSCRIBED") refresh(); });
    return () => { supabase.removeChannel(channel); };
  }, [id, refresh]);
  useRealtimeSelfHeal(refresh, !!id);


  return { source, request, loadPost, responses, loading, error, refresh };
}

export type HydratedQuoteResponse = DirectQuoteResponse & {
  broker_company_name: string | null;
  broker_company_verified: boolean;
  broker_avatar_path: string | null;
};

async function hydrateBrokerNames(
  resps: any[],
  setResponses: (v: HydratedQuoteResponse[]) => void,
) {
  const companyIds = Array.from(new Set(resps.map((r) => r.broker_company_id)));
  const userIds = Array.from(new Set(resps.map((r) => r.broker_user_id).filter(Boolean)));
  const nameMap = new Map<string, string>();
  const verifiedMap = new Map<string, boolean>();
  const avatarMap = new Map<string, string | null>();
  if (companyIds.length) {
    const { data: companies } = await supabase
      .from("companies").select("id,name,verification_status").in("id", companyIds);
    (companies ?? []).forEach((c: any) => {
      nameMap.set(c.id, c.name);
      verifiedMap.set(c.id, c.verification_status === "verified");
    });
  }
  if (userIds.length) {
    const { data: profs } = await supabase
      .from("profiles").select("id,avatar_url").in("id", userIds);
    (profs ?? []).forEach((p: any) => avatarMap.set(p.id, p.avatar_url ?? null));
  }
  setResponses(
    resps.map((r) => ({
      ...(r as DirectQuoteResponse),
      broker_company_name: displayCompanyName(nameMap.get(r.broker_company_id)) || null,
      broker_company_verified: verifiedMap.get(r.broker_company_id) ?? false,
      broker_avatar_path: avatarMap.get(r.broker_user_id) ?? null,
    }))
  );
}

/** Row shape for a direct_loads quote post the broker can see. */
export interface DirectQuoteLoadRow {
  id: string;
  origin_city: string;
  origin_state: string;
  origin_postal_code: string | null;
  destination_city: string;
  destination_state: string;
  destination_postal_code: string | null;
  equipment_type: string;
  weight_lbs: number | null;
  commodity: string | null;
  pickup_date: string | null;
  bid_deadline: string | null;
  shipper_notes_to_brokers: string | null;
  target_rate_min: number | null;
  target_rate_max: number | null;
  visibility_mode: string | null;
  status: string;
  created_at: string;
  posted_at: string | null;
}

/** Broker-facing: open RFQs + LR Direct quote posts, plus this broker's responses. */
export function useBrokerQuoteRequests(userId: string | undefined) {
  const [open, setOpen] = useState<DirectQuoteRequest[]>([]);
  const [history, setHistory] = useState<DirectQuoteRequest[]>([]);
  const [submitted, setSubmitted] = useState<(DirectQuoteResponse & { request: DirectQuoteRequest })[]>([]);
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());
  const [directOpen, setDirectOpen] = useState<DirectQuoteLoadRow[]>([]);
  const [directHistory, setDirectHistory] = useState<DirectQuoteLoadRow[]>([]);
  const [directSubmittedByLoad, setDirectSubmittedByLoad] = useState<Map<string, DirectQuoteResponse>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!userId) {
      setOpen([]); setHistory([]); setSubmitted([]); setHiddenIds(new Set());
      setDirectOpen([]); setDirectHistory([]); setDirectSubmittedByLoad(new Map());
      setLoading(false); return;
    }
    setLoading(true);
    const { data: reqs, error: rErr } = await supabase
      .from("direct_quote_requests")
      .select("*")
      .in("status", ["open", "closed", "expired"])
      .order("deadline", { ascending: false });
    if (rErr) { setError(rErr.message); setLoading(false); return; }
    const { data: myResp } = await supabase
      .from("direct_quote_responses")
      .select("*")
      .eq("broker_user_id", userId);
    const { data: hiddenRows } = await supabase
      .from("direct_quote_request_broker_hidden")
      .select("request_id")
      .eq("broker_user_id", userId);
    const hidden = new Set<string>((hiddenRows ?? []).map((h: any) => h.request_id as string));

    // LR Direct quote posts (post_type='quote') — RLS scopes visibility
    const { data: dLoads } = await supabase
      .from("direct_loads" as any)
      .select("id, origin_city, origin_state, origin_postal_code, destination_city, destination_state, destination_postal_code, equipment_type, weight_lbs, commodity, pickup_date, bid_deadline, shipper_notes_to_brokers, target_rate_min, target_rate_max, visibility_mode, status, created_at, posted_at")
      .eq("post_type", "quote")
      .is("deleted_at", null)
      .order("posted_at", { ascending: false });

    const mineByReq = new Map<string, DirectQuoteResponse>();
    const mineByLoad = new Map<string, DirectQuoteResponse>();
    (myResp ?? []).forEach((r: any) => {
      if (r.request_id) mineByReq.set(r.request_id, r as DirectQuoteResponse);
      else if (r.direct_load_id) mineByLoad.set(r.direct_load_id, r as DirectQuoteResponse);
    });

    const all = (reqs ?? []) as DirectQuoteRequest[];
    const now = Date.now();
    const isOpenLive = (r: DirectQuoteRequest) =>
      r.status === "open" && !r.archived_at
      && new Date(r.deadline).getTime() > now && !hidden.has(r.id);

    setOpen(all.filter(isOpenLive));
    setHistory(all.filter((r) => !isOpenLive(r)));
    setHiddenIds(hidden);
    setSubmitted(
      (myResp ?? [])
        .filter((r: any) => r.request_id)
        .map((r: any) => ({
          ...(r as DirectQuoteResponse),
          request: all.find((q) => q.id === r.request_id) as DirectQuoteRequest,
        })).filter((x) => x.request)
    );

    const loadRows = ((dLoads ?? []) as unknown as DirectQuoteLoadRow[]);
    const isDirectLive = (l: DirectQuoteLoadRow) =>
      l.status === "posted" && (!l.bid_deadline || new Date(l.bid_deadline).getTime() > now);
    setDirectOpen(loadRows.filter(isDirectLive));
    setDirectHistory(loadRows.filter((l) => !isDirectLive(l)));
    setDirectSubmittedByLoad(mineByLoad);

    setError(null);
    setLoading(false);
  }, [userId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Realtime — refresh on new quote posts or on the broker's own response changes
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`broker-quote-refresh-${userId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "direct_quote_responses" }, () => refresh())
      .on("postgres_changes", { event: "*", schema: "public", table: "direct_loads" }, (payload) => {
        if ((payload.new as any)?.post_type === "quote" || (payload.old as any)?.post_type === "quote") refresh();
      })
      .subscribe((status) => { if (status === "SUBSCRIBED") refresh(); });
    return () => { supabase.removeChannel(channel); };
  }, [userId, refresh]);
  useRealtimeSelfHeal(refresh, !!userId);


  return { open, history, submitted, hiddenIds, directOpen, directHistory, directSubmittedByLoad, loading, error, refresh };
}
